<template>
  <section class="card">
    <header class="head">
      <div>
        <h2>资讯管理</h2>
        <p class="tip">
          <strong>一级</strong>展示分区（宫格/产业服务/资讯动态）：鼠标移到左侧树一级行，点<strong>铅笔</strong>改名称；也可在
          <router-link to="/system/dict?typeCode=MP_DISPLAY_GROUP">字典管理</router-link>
          中维护类型 <strong>MP_DISPLAY_GROUP</strong>。
          <strong>二级</strong>频道（其下如「资讯动态3」）点频道行的铅笔编辑。
        </p>
      </div>
    </header>
    <p v-if="errorMsg" class="error">{{ errorMsg }}</p>

    <BannerCarouselPanel />

    <div class="layout" v-if="!loading">
      <aside class="tree-panel">
        <section v-for="group in treeGroups" :key="group.code" class="tree-l1">
          <div class="tree-l1-row" :class="{ 'tree-l1-row--active': isGroupSelected(group.code) }">
            <button type="button" class="tree-row tree-l1-btn" @click="onGroupClick(group)">
              <span class="chevron" :class="{ open: isGroupOpen(group.code) }">▶</span>
              <span class="tree-l1-name">{{ group.label }}</span>
            </button>
            <div class="tree-row-actions">
              <button
                type="button"
                class="tree-act-btn"
                title="修改一级分区名称"
                :disabled="!canContentEdit"
                @click.stop="openEditGroupLabel(group)"
              >
                <img class="tree-act-icon" :src="treeNavEditIcon" alt="修改分区名称">
              </button>
              <button
                type="button"
                class="tree-act-btn tree-act-btn--primary"
                title="新增"
                :disabled="!canContentEdit"
                @click.stop="openCreateChooserForGroup(group.code)"
              >
                <img class="tree-act-icon" :src="treeGroupAddIcon" alt="新增">
              </button>
            </div>
          </div>
          <Transition name="collapse">
            <div v-show="isGroupOpen(group.code)" class="tree-l1-body">
              <div v-for="cat in group.categories" :key="cat.id" class="tree-l2-wrap">
                <div
                  class="tree-l2-row"
                  :class="{ active: selectedCategory && selectedCategory.id === cat.id && !selectedNav }"
                >
                  <button
                    type="button"
                    class="tree-row tree-l2-btn"
                    @click="selectCategory(cat)"
                  >
                    <span
                      v-if="catHasNavs(cat)"
                      class="chevron"
                      :class="{ open: isCatOpen(cat.id) }"
                      @click.stop="toggleCat(cat.id)"
                    >▶</span>
                    <span v-else class="chevron-spacer" />
                    <img v-if="cat.coverUrl && cat.displayGroup !== 'NEWS'" class="tree-icon" :src="toFullUrl(cat.coverUrl)" alt="">
                    <span class="tree-cat-name">{{ cat.displayName }}</span>
                  </button>
                  <div class="tree-row-actions">
                    <button
                      type="button"
                      class="tree-act-btn"
                      title="编辑频道"
                      :disabled="!canContentEdit"
                      @click.stop="openEditCategoryFor(cat)"
                    >
                      <img class="tree-act-icon" :src="treeNavEditIcon" alt="编辑频道">
                    </button>
                    <button
                      v-if="cat.displayGroup !== 'NEWS'"
                      type="button"
                      class="tree-act-btn"
                      title="新增导航 Tab"
                      :disabled="!canContentEdit"
                      @click.stop="openCreateNavFor(cat)"
                    >
                      <img class="tree-act-icon" :src="treeChannelAddTabIcon" alt="新增导航 Tab">
                    </button>
                  </div>
                </div>
                <Transition v-if="catHasNavs(cat)" name="collapse">
                  <div v-show="isCatOpen(cat.id)" class="tree-l3-body">
                    <div
                      v-for="nav in cat.navs || []"
                      :key="nav.id"
                      class="tree-l3-row"
                      :class="{ active: selectedNav && selectedNav.id === nav.id }"
                    >
                      <button
                        type="button"
                        class="tree-row tree-l3-btn"
                        @click="selectNav(cat, nav)"
                      >
                        <span class="tree-leaf">└</span>
                        <span class="tree-nav-name">{{ nav.navTitle }}</span>
                        <small class="tree-count">{{ (nav.contents || []).length }} 条</small>
                      </button>
                      <div class="tree-row-actions">
                        <button
                          type="button"
                          class="tree-act-btn"
                          title="编辑 Tab"
                          :disabled="!canContentEdit"
                          @click.stop="openEditNavFor(cat, nav)"
                        >
                          <img class="tree-act-icon" :src="treeNavEditIcon" alt="编辑">
                        </button>
                        <button
                          type="button"
                          class="tree-act-btn tree-act-btn--danger"
                          title="删除 Tab"
                          :disabled="!canContentDelete"
                          @click.stop="onDeleteNav(nav)"
                        >
                          <img class="tree-act-icon" :src="treeNavDeleteIcon" alt="删除">
                        </button>
                      </div>
                    </div>
                  </div>
                </Transition>
              </div>
              <p v-if="!group.categories.length" class="tree-empty">暂无频道</p>
            </div>
          </Transition>
        </section>
      </aside>

      <div class="table-panel">
        <template v-if="selectedCategory">
          <div class="cat-bar">
            <div class="cat-meta">
              <strong>{{ panelTitle }}</strong>
              <small>{{ panelSubtitle }}</small>
            </div>
          </div>
          <div class="filters filters-panel content-filters">
            <input
              v-model.trim="titleFilterQ"
              class="flt-input flt-input--wide"
              placeholder="资讯标题"
              @keyup.enter="applyContentFilter"
            />
            <div class="filter-actions">
              <button type="button" class="btn btn-primary filter-action-btn" @click="applyContentFilter">查询</button>
              <button type="button" class="btn btn-cancel filter-action-btn" @click="resetContentFilter">重置</button>
            </div>
            <button class="btn btn-primary content-create-btn" :disabled="!canContentEdit" @click="openCreateContent">新建资讯</button>
          </div>
          <p v-if="contentLoadError" class="error">{{ contentLoadError }}</p>
          <div class="table-scroll">
            <table class="content-table">
              <thead>
                <tr>
                  <th class="center" style="width:52px">序号</th>
                  <th class="center" style="width:72px">封面</th>
                  <th class="center">标题</th>
                  <th class="center" style="width:88px">类型</th>
                  <th class="center" style="width:96px">浏览量</th>
                  <th class="center" style="width:150px">发布时间</th>
                  <th class="center" style="width:150px">创建时间</th>
                  <th class="col-actions center" style="width:140px">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, idx) in pagedContents" :key="row.id">
                  <td class="center">{{ contentRowOffset + idx + 1 }}</td>
                  <td class="center"><img v-if="row.coverUrl" class="thumb" :src="toFullUrl(row.coverUrl)" alt=""></td>
                  <td class="center ellipsis">{{ row.title }}</td>
                  <td class="center">{{ contentTypeLabel(row.contentType) }}</td>
                  <td class="center">{{ viewsDisplay(row) }}</td>
                  <td class="center">{{ formatDt(row.publishAt) }}</td>
                  <td class="center">{{ formatDt(row.createdAt) }}</td>
                  <td class="col-actions center">
                    <button class="btn btn-edit" :disabled="!canContentEdit" @click="openEditContent(row)">编辑</button>
                    <button class="btn btn-delete" :disabled="!canContentDelete" @click="onDeleteContent(row)">删除</button>
                  </td>
                </tr>
                <tr v-if="!filteredContents.length"><td colspan="7" class="muted center">{{ emptyContentHint }}</td><td class="col-actions col-actions--empty" /></tr>
              </tbody>
            </table>
          </div>
          <ListPager
            v-model:page="contentPage"
            v-model:page-size="contentPageSize"
            :total="filteredContents.length"
          />
        </template>
        <template v-else-if="selectedGroupCode">
          <div class="cat-bar">
            <div class="cat-meta">
              <strong>{{ groupLabel(selectedGroupCode) }}</strong>
              <small>{{ groupPanelSubtitle }}</small>
            </div>
            <button class="btn btn-primary" :disabled="!canContentEdit" @click="openCreateChooserForGroup(selectedGroupCode)">新建频道</button>
          </div>
          <p class="muted center pad">{{ groupEmptyHint }}</p>
        </template>
        <p v-else class="muted center pad">请在左侧选择频道或导航 Tab</p>
      </div>
    </div>
    <p v-else class="muted center pad">加载中…</p>

    <!-- 一级展示分区名称 -->
    <div v-if="showGroupLabelDialog" class="mask" @click.self="showGroupLabelDialog = false">
      <div class="dialog dialog--shell dialog--narrow">
        <div class="dialog-head">
          <h3 class="dialog-title">修改一级分区名称</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showGroupLabelDialog = false">×</button>
        </div>
        <div class="dialog-body">
          <p class="field-hint">
            修改「{{ groupLabelForm.codeHint }}」在后台树与小程序首页的显示名称（如「资讯动态」改为「协会资讯」）。
          </p>
          <label class="field full">
            <span>分区名称</span>
            <input v-model.trim="groupLabelForm.label" maxlength="200" placeholder="请输入名称">
          </label>
          <p v-if="groupLabelError" class="error">{{ groupLabelError }}</p>
        </div>
        <div class="dialog-foot">
          <button type="button" class="btn btn-cancel" @click="showGroupLabelDialog = false">取消</button>
          <button type="button" class="btn btn-primary" :disabled="groupLabelSaving" @click="saveGroupLabel">
            {{ groupLabelSaving ? '保存中…' : '保存' }}
          </button>
        </div>
      </div>
    </div>

    <!-- 新增向导 -->
    <div v-if="showCreateChooser" class="mask mask--create" @click.self="showCreateChooser = false">
      <div class="dialog dialog--create dialog--shell">
        <div class="dialog-head">
          <h3 class="dialog-title">新增</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showCreateChooser = false">×</button>
        </div>
        <div class="dialog-body">
          <div class="form-grid form-grid--create">
            <label class="field">
              <span>一级 · 展示分区</span>
              <FancySelect v-model="createChooser.displayGroup" :options="displayGroupOptions" placeholder="选择展示分区" />
            </label>
            <label class="field">
              <span>二级 · 归属频道（可选）</span>
              <FancySelect
                v-model="createChooser.parentCategoryId"
                :options="createChooserCategoryOptions"
                placeholder="不选 → 新建二级频道"
                searchable
              />
            </label>
            <label v-if="createChooser.parentCategoryId && !createChooserIsNews" class="field full">
              <span>三级 · 归属导航 Tab（可选）</span>
              <FancySelect
                v-model="createChooser.parentNavId"
                :options="createChooserNavOptions"
                placeholder="不选 → 在频道下直接新增内容"
                searchable
              />
            </label>
            <label v-if="createChooser.parentCategoryId && !createChooser.parentNavId && !createChooserIsNews" class="field full">
              <span>或</span>
              <button type="button" class="btn btn-edit" @click="createChooserNavFromWizard">新建导航 Tab</button>
            </label>
          </div>
        </div>
        <div class="dialog-foot">
          <button class="btn btn-cancel" @click="showCreateChooser = false">取消</button>
          <button class="btn btn-primary" @click="confirmCreateChooser">下一步</button>
        </div>
      </div>
    </div>

    <!-- 三级导航 Tab -->
    <div v-if="showNavDialog" class="mask" @click.self="showNavDialog = false">
      <div class="dialog dialog--shell">
        <div class="dialog-head">
          <h3 class="dialog-title">{{ navForm.id ? '编辑导航 Tab' : '新建导航 Tab' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showNavDialog = false">×</button>
        </div>
        <div class="dialog-body">
          <p v-if="navDialogError" class="dialog-error">{{ navDialogError }}</p>
          <div class="form-grid">
            <label class="field full">
              <span>Tab 名称<span class="req">*</span></span>
              <input v-model="navForm.navTitle" placeholder="如：协会简介">
            </label>
            <label class="field"><span>排序</span><input v-model.number="navForm.sortOrder" type="number"></label>
            <div class="field"><span>状态</span>
              <div class="status-toggle">
                <button type="button" class="btn btn-edit btn-sm" :class="{ active: navForm.statusCode === 'ENABLED' }" @click="navForm.statusCode = 'ENABLED'">启用</button>
                <button type="button" class="btn btn-edit btn-sm" :class="{ active: navForm.statusCode === 'DISABLED' }" @click="navForm.statusCode = 'DISABLED'">停用</button>
              </div>
            </div>
          </div>
        </div>
        <div class="dialog-foot dialog-foot--split">
          <div class="dialog-foot-left">
            <button
              v-if="navForm.id"
              type="button"
              class="btn btn-delete"
              :disabled="!canContentDelete"
              @click="onDeleteNav({ id: navForm.id, navTitle: navForm.navTitle })"
            >删除 Tab</button>
          </div>
          <div class="dialog-foot-actions">
            <button type="button" class="btn btn-cancel" @click="showNavDialog = false">取消</button>
            <button type="button" class="btn btn-primary" :disabled="saving" @click="saveNav">{{ saving ? '保存中…' : '保存' }}</button>
          </div>
        </div>
      </div>
    </div>

    <!-- 一级栏目 -->
    <div v-if="showCategoryDialog" class="mask" @click.self="showCategoryDialog = false">
      <div class="dialog wide dialog--shell">
        <div class="dialog-head">
          <h3 class="dialog-title">{{ categoryForm.id ? '编辑二级频道' : '新建二级频道' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showCategoryDialog = false">×</button>
        </div>
        <div class="dialog-body">
          <p v-if="categoryDialogError" class="dialog-error">{{ categoryDialogError }}</p>
          <div class="form-grid">
            <label class="field">
              <span>一级 · 展示分区<span class="req">*</span></span>
              <FancySelect v-model="categoryForm.displayGroup" :options="displayGroupOptions" placeholder="选择展示分区" />
            </label>
            <label class="field">
              <span>list_code（留空自动生成）</span>
              <input v-model="categoryForm.listCode" placeholder="如 about、join_apply">
            </label>
            <label class="field full">
              <span>分类名称<span class="req">*</span></span>
              <input v-model="categoryForm.displayName" @blur="maybeFillListCode">
            </label>
            <div v-if="categoryForm.displayGroup !== 'NEWS'" class="field full">
              <span>分类图标（宫格/产业服务入口图）<span class="req">*</span></span>
              <ImageUploadField v-model="categoryForm.coverUrl" />
            </div>
            <p v-if="categoryForm.displayGroup !== 'NEWS' && categoryForm.listCode" class="field full link-preview">
              小程序跳转：<code>{{ previewArticleLink }}</code>
            </p>
            <label class="field"><span>排序</span><input v-model.number="categoryForm.sortOrder" type="number"></label>
            <div class="field">
              <span>状态</span>
              <div class="status-toggle">
                <button type="button" class="btn btn-edit btn-sm" :class="{ active: categoryForm.statusCode === 'ENABLED' }" @click="categoryForm.statusCode = 'ENABLED'">启用</button>
                <button type="button" class="btn btn-edit btn-sm" :class="{ active: categoryForm.statusCode === 'DISABLED' }" @click="categoryForm.statusCode = 'DISABLED'">停用</button>
              </div>
            </div>
            <div class="field full">
              <span>查看身份</span>
              <div class="status-toggle">
                <button type="button" class="btn btn-edit btn-sm" :class="{ active: categoryForm.openToNonMember === 1 }" @click="categoryForm.openToNonMember = 1">不限制</button>
                <button type="button" class="btn btn-edit btn-sm" :class="{ active: categoryForm.openToNonMember === 0 }" @click="categoryForm.openToNonMember = 0">仅正式会员</button>
              </div>
            </div>
          </div>
        </div>
        <div class="dialog-foot">
          <button class="btn btn-cancel" @click="showCategoryDialog = false">取消</button>
          <button class="btn btn-primary" :disabled="saving" @click="saveCategory">{{ saving ? '保存中…' : '保存' }}</button>
        </div>
      </div>
    </div>

    <!-- 二级条目 -->
    <div v-if="showArticleDialog" class="mask mask--article-editor" @click.self="showArticleDialog = false">
      <div class="dialog wide dialog--shell dialog--with-editor" :class="{ 'dialog--rich-split': articleForm.contentType === 'RICH_TEXT' }">
        <div class="dialog-head">
          <h3 class="dialog-title">{{ articleForm.id ? '编辑内容' : '新增内容' }}</h3>
          <button type="button" class="dialog-close" aria-label="关闭" @click="showArticleDialog = false">×</button>
        </div>
        <div class="dialog-body dialog-body--with-editor">
          <p v-if="articleDialogError" class="dialog-error">{{ articleDialogError }}</p>
          <div class="form-grid">
            <label v-if="articleForm.navId" class="field full">
              <span>所属导航 Tab</span>
              <input :value="selectedNav ? selectedNav.navTitle : ''" disabled>
            </label>
            <label class="field full">
              <span>标题<span class="req">*</span></span>
              <input v-model="articleForm.title">
            </label>
            <label class="field full"><span>副标题</span><input v-model="articleForm.excerpt" placeholder="可空"></label>
            <div class="field full">
              <span>封面图</span>
              <ImageUploadField v-model="articleForm.coverUrl" />
            </div>
            <div class="field full">
              <span>内容类型<span class="req">*</span></span>
              <div class="status-toggle">
                <button
                  type="button"
                  class="btn btn-edit btn-sm"
                  :class="{ active: articleForm.contentType === 'RICH_TEXT', disabled: isNewsArticle }"
                  :disabled="isNewsArticle"
                  @click="setArticleContentType('RICH_TEXT')"
                >图文</button>
                <button type="button" class="btn btn-edit btn-sm" :class="{ active: articleForm.contentType === 'ARTICLE' }" @click="setArticleContentType('ARTICLE')">公众号</button>
                <button type="button" class="btn btn-edit btn-sm" :class="{ active: articleForm.contentType === 'DOC' }" @click="setArticleContentType('DOC')">文档</button>
              </div>
            </div>
            <label v-if="articleForm.contentType === 'ARTICLE'" class="field full">
              <span>公众号链接<span class="req">*</span></span>
              <div class="url-row">
                <input v-model="articleForm.wechatUrl" placeholder="https://mp.weixin.qq.com/..." @blur="tryResolveTitle(true)">
                <button type="button" class="btn btn-edit" :disabled="resolvingTitle" @click="tryResolveTitle(false)">
                  {{ resolvingTitle ? '识别中…' : '识别标题' }}
                </button>
              </div>
            </label>
            <div v-if="articleForm.contentType === 'DOC'" class="field full">
              <span>文档文件<span class="req">*</span></span>
              <input ref="docInput" class="visually-hidden-file" type="file" accept=".pdf,.doc,.docx,.xls,.xlsx" @change="onDocFile">
              <div class="upload-row">
                <button type="button" class="btn btn-edit" @click="pickDoc">上传文档</button>
                <a v-if="articleForm.fileUrl" class="file-link" :href="toFullUrl(articleForm.fileUrl)" target="_blank" rel="noopener">已上传文件</a>
              </div>
            </div>
            <div v-if="articleForm.contentType === 'RICH_TEXT'" class="field full field--rich-editor">
              <span>图文详情</span>
              <p class="field-hint">135/秀米 HTML 可在「HTML 源码」粘贴后切回「可视化」；工具栏含源码/全屏，版式与图片可直接在编辑区修改</p>
              <RichTextEditor
                :key="'article-rte-' + (articleForm.id || 'new')"
                v-model="articleForm.bodyHtml"
              />
            </div>
            <div class="form-grid-meta">
            <label class="field">
              <span>发布时间<span class="req">*</span></span>
              <el-date-picker
                v-model="articleForm.publishAt"
                type="datetime"
                value-format="YYYY-MM-DDTHH:mm:ss"
                format="YYYY-MM-DD HH:mm:ss"
                placeholder="选择发布时间"
                style="width: 100%"
              />
            </label>
            <label class="field"><span>虚拟浏览量</span><input v-model.number="articleForm.viewCount" type="number" min="0"></label>
            <label class="field"><span>排序</span><input v-model.number="articleForm.sortOrder" type="number"></label>
            <div class="field">
              <span>状态</span>
              <div class="status-toggle">
                <button type="button" class="btn btn-edit btn-sm" :class="{ active: articleForm.statusCode === 'ENABLED' }" @click="articleForm.statusCode = 'ENABLED'">启用</button>
                <button type="button" class="btn btn-edit btn-sm" :class="{ active: articleForm.statusCode === 'DISABLED' }" @click="articleForm.statusCode = 'DISABLED'">停用</button>
              </div>
            </div>
            <div class="field full">
              <span>查看身份</span>
              <div class="status-toggle">
                <button type="button" class="btn btn-edit btn-sm" :class="{ active: articleForm.openToNonMember === 1 }" @click="articleForm.openToNonMember = 1">不限制</button>
                <button type="button" class="btn btn-edit btn-sm" :class="{ active: articleForm.openToNonMember === 0 }" @click="articleForm.openToNonMember = 0">仅正式会员</button>
              </div>
            </div>
            </div>
          </div>
        </div>
        <div class="dialog-foot">
          <button class="btn btn-cancel" @click="showArticleDialog = false">取消</button>
          <button class="btn btn-primary" :disabled="saving" @click="saveArticle">{{ saving ? '保存中…' : '保存' }}</button>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { adminConfirm } from '@/utils/adminDialog'
import {
  BASE_URL,
  createArticle,
  deleteArticle,
  getArticleManageTree,
  getArticles,
  getDictItems,
  getDictTypes,
  createDictItem,
  updateDictItem,
  resolveArticleTitleByUrl,
  saveArticleManageCategory,
  saveArticleManageNav,
  deleteArticleManageNav,
  updateArticle,
  uploadFile,
} from '@/api'
import BannerCarouselPanel from '@/components/cms/BannerCarouselPanel.vue'
import ImageUploadField from '@/components/cms/ImageUploadField.vue'
import RichTextEditor from '@/components/cms/RichTextEditor.vue'
import FancySelect from '@/components/FancySelect.vue'
import ListPager from '@/components/ListPager.vue'
import { ElDatePicker } from 'element-plus'
import 'element-plus/es/components/date-picker/style/css'
import { formatDateTimeForDisplay } from '@/utils/formatDateTime'
import { normalizeRichHtmlBody } from '@/utils/richHtmlNormalize'
import treeNavEditIcon from '@/assets/icons/tree-nav-edit.svg'
import treeNavDeleteIcon from '@/assets/icons/tree-nav-delete.svg'
import treeChannelAddTabIcon from '@/assets/icons/tree-channel-add-tab.svg'
import treeGroupAddIcon from '@/assets/icons/tree-group-add.svg'
import { hasAnyPermission } from '@/utils/adminPermission'
import { clampPage, pageRowOffset, slicePageRows } from '@/utils/listPager'

const GROUP_LABELS = { QUICK: '宫格', SERVICE: '产业服务', NEWS: '资讯动态' }
const MP_DISPLAY_GROUP_TYPE = 'MP_DISPLAY_GROUP'
const buildDisplayGroupOptions = (labels) => [
  { value: 'QUICK', label: (labels && labels.QUICK) || GROUP_LABELS.QUICK },
  { value: 'SERVICE', label: (labels && labels.SERVICE) || GROUP_LABELS.SERVICE },
  { value: 'NEWS', label: (labels && labels.NEWS) || GROUP_LABELS.NEWS },
]
const CONTENT_LABELS = { RICH_TEXT: '图文', ARTICLE: '文章', DOC: '文档' }

const emptyCategoryForm = () => ({
  id: null,
  listCode: '',
  displayName: '',
  coverUrl: '',
  displayGroup: 'QUICK',
  sortOrder: 0,
  statusCode: 'ENABLED',
  openToNonMember: 1,
  remark: '',
})

const emptyNavForm = (categoryId) => ({
  id: null,
  categoryId: categoryId || null,
  navTitle: '',
  sortOrder: 0,
  statusCode: 'ENABLED',
  openToNonMember: 1,
  remark: '',
})

const nowIsoLocal = () => {
  const d = new Date()
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`
}

const emptyArticleForm = (categoryId, navId, displayGroup) => ({
  id: null,
  categoryId: categoryId || null,
  navId: navId || null,
  title: '',
  excerpt: '',
  coverUrl: '',
  contentType: displayGroup === 'NEWS' ? 'ARTICLE' : 'RICH_TEXT',
  wechatUrl: '',
  fileUrl: '',
  bodyHtml: '',
  publishAt: nowIsoLocal(),
  viewCount: 0,
  sortOrder: 0,
  statusCode: 'ENABLED',
  openToNonMember: 1,
  remark: '',
})

export default {
  name: 'ArticleManageMergedView',
  components: { BannerCarouselPanel, ImageUploadField, RichTextEditor, FancySelect, ListPager, ElDatePicker },
  data() {
    return {
      loading: true,
      saving: false,
      resolvingTitle: false,
      errorMsg: '',
      categoryDialogError: '',
      navDialogError: '',
      articleDialogError: '',
      treePayload: null,
      expandedGroups: { QUICK: true, SERVICE: true, NEWS: true },
      expandedCats: {},
      selectedCategory: null,
      selectedNav: null,
      selectedGroupCode: null,
      contentRows: [],
      contentPage: 1,
      contentPageSize: 10,
      contentLoadError: '',
      titleFilterQ: '',
      appliedTitleFilter: '',
      showCreateChooser: false,
      createChooser: { displayGroup: 'QUICK', parentCategoryId: null, parentNavId: null },
      showCategoryDialog: false,
      categoryForm: emptyCategoryForm(),
      showNavDialog: false,
      navForm: emptyNavForm(),
      showArticleDialog: false,
      articleForm: emptyArticleForm(),
      canContentEdit: false,
      canContentDelete: false,
      treeNavEditIcon,
      treeNavDeleteIcon,
      treeChannelAddTabIcon,
      treeGroupAddIcon,
      displayGroupLabels: { ...GROUP_LABELS },
      displayGroupTypeId: null,
      displayGroupItems: [],
      showGroupLabelDialog: false,
      groupLabelSaving: false,
      groupLabelError: '',
      groupLabelForm: { code: '', codeHint: '', label: '' },
    }
  },
  computed: {
    treeGroups() {
      return this.treePayload?.groups || []
    },
    allCategories() {
      return this.treeGroups.flatMap((g) => g.categories || [])
    },
    filteredContents() {
      const q = (this.appliedTitleFilter || '').trim().toLowerCase()
      if (!q) return this.contentRows
      return this.contentRows.filter((r) => (r.title || '').toLowerCase().includes(q))
    },
    contentRowOffset() {
      return pageRowOffset(this.contentPage, this.contentPageSize)
    },
    pagedContents() {
      return slicePageRows(this.filteredContents, this.contentPage, this.contentPageSize)
    },
    panelTitle() {
      if (!this.selectedCategory) return ''
      if (this.selectedNav) {
        return `${this.selectedCategory.displayName} / ${this.selectedNav.navTitle}`
      }
      return this.selectedCategory.displayName
    },
    panelSubtitle() {
      if (this.isNewsCategory(this.selectedCategory)) return '资讯动态频道内容；保存即发布'
      if (this.selectedNav) return '三级导航 Tab 下的内容'
      if (this.catHasNavs(this.selectedCategory)) {
        return '频道直属内容；也可在左侧选择导航 Tab'
      }
      return '频道直属内容；需要顶部 Tab 时点「新增导航 Tab」'
    },
    emptyContentHint() {
      if (this.isNewsCategory(this.selectedCategory)) return '暂无内容，可点「新建资讯」'
      if (this.selectedNav) return '该 Tab 下暂无内容'
      return '暂无内容，可点「新建资讯」或先建导航 Tab'
    },
    createChooserIsNews() {
      if (this.createChooser.displayGroup === 'NEWS') return true
      const cat = this.allCategories.find((c) => c.id === this.createChooser.parentCategoryId)
      return this.isNewsCategory(cat)
    },
    isNewsArticle() {
      const cat = this.allCategories.find((c) => c.id === this.articleForm.categoryId)
      return this.isNewsCategory(cat)
    },
    groupPanelSubtitle() {
      const cats = this.categoriesInGroup(this.selectedGroupCode)
      if (!cats.length) return '暂无二级频道，请先新建'
      return '请点选下方具体频道，或在有唯一频道时自动进入内容列表'
    },
    groupEmptyHint() {
      const cats = this.categoriesInGroup(this.selectedGroupCode)
      if (!cats.length) return '该分区下还没有频道，请点击右上角「新建频道」'
      return '请在左侧树中点选具体频道（二级），右侧将显示内容列表与「新建资讯」'
    },
    previewArticleLink() {
      const code = (this.categoryForm.listCode || '').trim() || '（保存后生成）'
      const title = encodeURIComponent((this.categoryForm.displayName || '').trim())
      return `/pages/article/list?code=${code}&title=${title}`
    },
    displayGroupOptions() {
      return buildDisplayGroupOptions(this.displayGroupLabels)
    },
    createChooserCategoryOptions() {
      return [
        { value: null, label: '不选 → 新建二级频道' },
        ...this.categoriesInGroup(this.createChooser.displayGroup).map((c) => ({
          value: c.id,
          label: c.displayName,
        })),
      ]
    },
    createChooserNavOptions() {
      return [
        { value: null, label: '不选 → 在频道下直接新增内容' },
        ...this.navsInCategory(this.createChooser.parentCategoryId).map((n) => ({
          value: n.id,
          label: n.navTitle,
        })),
      ]
    },
  },
  watch: {
    'filteredContents.length'(n) {
      this.contentPage = clampPage(this.contentPage, n, this.contentPageSize)
    },
    'createChooser.displayGroup'() {
      this.createChooser.parentCategoryId = null
      this.createChooser.parentNavId = null
    },
    'createChooser.parentCategoryId'() {
      this.createChooser.parentNavId = null
    },
  },
  async mounted() {
    this.canContentEdit = hasAnyPermission(['content.manage', 'content.edit'])
    this.canContentDelete = hasAnyPermission(['content.manage', 'content.delete'])
    try {
      await this.loadDisplayGroupLabels()
    } catch {
      // 使用默认分区名
    }
    await this.reload()
    this.tryAutoSelectNewsChannel()
  },
  methods: {
    async loadDisplayGroupLabels() {
      const types = await getDictTypes()
      const type = (types || []).find((t) => String(t.typeCode || '') === MP_DISPLAY_GROUP_TYPE)
      if (!type || type.id == null) return
      this.displayGroupTypeId = type.id
      const items = await getDictItems(type.id)
      this.displayGroupItems = items || []
      const labels = { ...GROUP_LABELS }
      for (const it of this.displayGroupItems) {
        if (String(it.statusCode || '').toUpperCase() !== 'ENABLED') continue
        const code = String(it.itemCode || it.itemValue || '').trim()
        const label = String(it.itemLabel || '').trim()
        if (code && label) labels[code] = label
      }
      this.displayGroupLabels = labels
    },
    groupLabel(code) {
      return (this.displayGroupLabels && this.displayGroupLabels[code]) || GROUP_LABELS[code] || code || '—'
    },
    openEditGroupLabel(group) {
      if (!group || !group.code) return
      const code = group.code
      this.groupLabelForm = {
        code,
        codeHint: this.groupLabel(code),
        label: String(group.label || this.groupLabel(code) || '').trim(),
      }
      this.groupLabelError = ''
      this.showGroupLabelDialog = true
    },
    async saveGroupLabel() {
      const code = this.groupLabelForm.code
      const label = String(this.groupLabelForm.label || '').trim()
      if (!label) {
        this.groupLabelError = '名称不能为空'
        return
      }
      if (!this.displayGroupTypeId) {
        this.groupLabelError = '未找到字典类型 MP_DISPLAY_GROUP，请先在字典管理中创建'
        return
      }
      this.groupLabelSaving = true
      this.groupLabelError = ''
      try {
        const existing = (this.displayGroupItems || []).find(
          (it) => String(it.itemCode || it.itemValue || '').trim() === code
        )
        const payload = {
          typeId: this.displayGroupTypeId,
          typeCode: MP_DISPLAY_GROUP_TYPE,
          itemCode: code,
          itemLabel: label,
          sortOrder: existing?.sortOrder || 0,
          statusCode: existing?.statusCode || 'ENABLED',
          remark: existing?.remark || '',
        }
        if (existing?.id != null) await updateDictItem(existing.id, payload)
        else await createDictItem(payload)
        await this.loadDisplayGroupLabels()
        this.showGroupLabelDialog = false
        await this.reload({ silent: true, resetPage: false })
      } catch (e) {
        this.groupLabelError = e.message || '保存失败'
      } finally {
        this.groupLabelSaving = false
      }
    },
    contentTypeLabel(t) {
      return CONTENT_LABELS[t] || t || '—'
    },
    formatDt(v) {
      return formatDateTimeForDisplay(v)
    },
    viewsDisplay(row) {
      const actual = row.actualViewCount ?? 0
      const virtual = row.viewCount ?? 0
      return `${actual}/${virtual}`
    },
    applyContentFilter() {
      this.appliedTitleFilter = String(this.titleFilterQ || '').trim()
      this.contentPage = 1
    },
    resetContentFilter() {
      this.titleFilterQ = ''
      this.appliedTitleFilter = ''
      this.contentPage = 1
    },
    isNewsCategory(cat) {
      return !!(cat && cat.displayGroup === 'NEWS')
    },
    categoryDisplayGroup(categoryId) {
      const cat = this.allCategories.find((c) => c.id === categoryId)
      return cat?.displayGroup || this.selectedCategory?.displayGroup || 'QUICK'
    },
    setArticleContentType(type) {
      if (type === 'RICH_TEXT' && this.isNewsArticle) return
      this.articleForm.contentType = type
    },
    tryAutoSelectNewsChannel() {
      if (this.selectedCategory) return
      const newsCats = this.categoriesInGroup('NEWS')
      if (newsCats.length === 1) {
        this.selectedGroupCode = 'NEWS'
        this.selectCategory(newsCats[0])
      }
    },
    toFullUrl(url) {
      const u = String(url || '').trim()
      if (!u) return ''
      if (/^https?:\/\//i.test(u) || u.startsWith('data:')) return u
      const base = String(BASE_URL || '').replace(/\/$/, '')
      if (base) return `${base}${u.startsWith('/') ? u : `/${u}`}`
      if (typeof window !== 'undefined' && window.location?.origin) {
        return `${window.location.origin}${u.startsWith('/') ? u : `/${u}`}`
      }
      return u
    },
    categoriesInGroup(group) {
      return this.allCategories.filter((c) => c.displayGroup === group)
    },
    navsInCategory(categoryId) {
      const cat = this.allCategories.find((c) => c.id === categoryId)
      return cat?.navs || []
    },
    findNavById(navId) {
      if (!navId) return null
      for (const cat of this.allCategories) {
        const hit = (cat.navs || []).find((n) => n.id === navId)
        if (hit) return hit
      }
      return null
    },
    isGroupOpen(code) {
      return this.expandedGroups[code] !== false
    },
    isCatOpen(id) {
      return !!this.expandedCats[id]
    },
    toggleGroup(code) {
      this.expandedGroups = { ...this.expandedGroups, [code]: !this.isGroupOpen(code) }
    },
    isGroupSelected(code) {
      if (this.selectedCategory?.displayGroup === code) return true
      return this.selectedGroupCode === code && !this.selectedCategory
    },
    onGroupClick(group) {
      const willOpen = !this.isGroupOpen(group.code)
      this.toggleGroup(group.code)
      this.selectedGroupCode = group.code
      this.selectedNav = null
      const cats = group.categories || []
      if (willOpen && cats.length === 1) {
        this.selectCategory(cats[0])
        return
      }
      if (cats.length !== 1) {
        this.selectedCategory = null
        this.contentRows = []
        this.contentLoadError = ''
      }
    },
    toggleCat(id) {
      this.expandedCats = { ...this.expandedCats, [id]: !this.isCatOpen(id) }
    },
    catHasNavs(cat) {
      if (this.isNewsCategory(cat)) return false
      return !!(cat && Array.isArray(cat.navs) && cat.navs.length)
    },
    async syncContentRows({ resetPage = true } = {}) {
      if (!this.selectedCategory) {
        this.contentRows = []
        this.contentLoadError = ''
        return
      }
      this.contentLoadError = ''
      const isNews = this.isNewsCategory(this.selectedCategory)
      try {
        const rows = await getArticles(this.selectedCategory.id)
        const navId = this.selectedNav?.id || null
        this.contentRows = (rows || [])
          .filter((r) => {
            if (isNews) return true
            if (navId) return r.navId === navId
            return !r.navId
          })
          .sort((a, b) => {
            const sa = a.sortOrder ?? 0
            const sb = b.sortOrder ?? 0
            if (sa !== sb) return sa - sb
            return (b.id || 0) - (a.id || 0)
          })
        if (resetPage) this.contentPage = 1
      } catch (e) {
        this.contentRows = []
        this.contentLoadError = e.message || '加载内容失败'
      }
    },
    /** silent: 不卸掉布局（避免 .content-body 滚动条被夹回顶部）；resetPage: 是否重置内容列表页码 */
    async reload({ silent = false, resetPage = true } = {}) {
      if (!silent) this.loading = true
      this.errorMsg = ''
      try {
        this.treePayload = await getArticleManageTree()
        if (this.selectedCategory) {
          const fresh = this.allCategories.find((c) => c.id === this.selectedCategory.id)
          if (fresh) {
            this.selectedCategory = fresh
            if (this.selectedNav) {
              const nav = (fresh.navs || []).find((n) => n.id === this.selectedNav.id)
              this.selectedNav = nav || null
            }
            await this.syncContentRows({ resetPage })
          } else {
            this.selectedCategory = null
            this.selectedNav = null
            this.contentRows = []
          }
        }
      } catch (e) {
        this.errorMsg = e.message || '加载失败'
      } finally {
        if (!silent) this.loading = false
      }
    },
    selectCategory(cat) {
      this.selectedCategory = cat
      this.selectedNav = null
      this.selectedGroupCode = cat?.displayGroup || null
      if (this.catHasNavs(cat)) {
        this.expandedCats = { ...this.expandedCats, [cat.id]: true }
      }
      this.syncContentRows({ resetPage: true })
    },
    selectNav(cat, nav) {
      this.selectedCategory = cat
      this.selectedNav = nav
      this.selectedGroupCode = cat?.displayGroup || null
      this.expandedCats = { ...this.expandedCats, [cat.id]: true }
      this.syncContentRows({ resetPage: true })
    },
    createChooserNavFromWizard() {
      const cid = this.createChooser.parentCategoryId
      if (!cid) return
      this.showCreateChooser = false
      this.navForm = emptyNavForm(cid)
      this.showNavDialog = true
    },
    openCreateChooser() {
      const group = this.selectedCategory?.displayGroup || 'QUICK'
      this.openCreateChooserForGroup(group, {
        parentCategoryId: this.selectedCategory?.id || null,
        parentNavId: this.selectedNav?.id || null,
      })
    },
    openCreateChooserForGroup(groupCode, preset = {}) {
      this.expandedGroups = { ...this.expandedGroups, [groupCode]: true }
      this.createChooser = {
        displayGroup: groupCode,
        parentCategoryId: preset.parentCategoryId ?? null,
        parentNavId: preset.parentNavId ?? null,
      }
      this.showCreateChooser = true
    },
    confirmCreateChooser() {
      this.showCreateChooser = false
      const { displayGroup, parentCategoryId, parentNavId } = this.createChooser
      const navId = this.createChooserIsNews ? null : parentNavId
      if (navId) {
        this.articleForm = emptyArticleForm(parentCategoryId, navId, displayGroup)
        this.articleDialogError = ''
        this.showArticleDialog = true
        return
      }
      if (parentCategoryId) {
        this.articleForm = emptyArticleForm(parentCategoryId, null, this.categoryDisplayGroup(parentCategoryId))
        this.articleDialogError = ''
        this.showArticleDialog = true
        return
      }
      const f = emptyCategoryForm()
      f.displayGroup = displayGroup
      this.categoryForm = f
      this.categoryDialogError = ''
      this.showCategoryDialog = true
    },
    openCreateNav() {
      if (!this.selectedCategory) return
      this.openCreateNavFor(this.selectedCategory)
    },
    openCreateNavFor(cat) {
      if (!cat || this.isNewsCategory(cat)) return
      this.selectedCategory = cat
      this.selectedNav = null
      this.expandedCats = { ...this.expandedCats, [cat.id]: true }
      this.navForm = emptyNavForm(cat.id)
      this.navDialogError = ''
      this.showNavDialog = true
    },
    openEditCategoryFor(cat) {
      if (!cat) return
      this.selectedCategory = cat
      this.selectedNav = null
      this.openEditCategory(cat)
    },
    openEditNavFor(cat, nav) {
      this.selectedCategory = cat
      this.selectedNav = nav
      this.expandedCats = { ...this.expandedCats, [cat.id]: true }
      this.openEditNav(nav)
    },
    openEditNav(nav) {
      this.navForm = {
        id: nav.id,
        categoryId: this.selectedCategory?.id,
        navTitle: nav.navTitle || '',
        sortOrder: nav.sortOrder ?? 0,
        statusCode: nav.statusCode || 'ENABLED',
        openToNonMember: nav.openToNonMember ?? 1,
        remark: '',
      }
      this.navDialogError = ''
      this.showNavDialog = true
    },
    async saveNav() {
      const f = this.navForm
      this.navDialogError = ''
      if (!f.categoryId || !f.navTitle?.trim()) {
        this.navDialogError = '请填写 Tab 名称'
        return
      }
      this.saving = true
      try {
        await saveArticleManageNav({
          id: f.id,
          categoryId: f.categoryId,
          navTitle: f.navTitle.trim(),
          sortOrder: f.sortOrder ?? 0,
          statusCode: f.statusCode,
          openToNonMember: f.openToNonMember,
          remark: f.remark || '',
        }, !!f.id)
        this.showNavDialog = false
        await this.reload({ silent: true, resetPage: false })
      } catch (e) {
        this.errorMsg = e.message || '保存失败'
      } finally {
        this.saving = false
      }
    },
    async onDeleteNav(nav) {
      let target = nav || this.selectedNav
      if (!target || !target.id) return
      const fromTree = this.findNavById(target.id)
      if (fromTree) target = fromTree
      const title = String(target.navTitle || '').trim() || '该 Tab'
      const count = Array.isArray(target.contents) ? target.contents.length : 0
      const extra = count > 0 ? `\n\n其下还有 ${count} 条内容，删除 Tab 后这些内容将不再显示在该 Tab 下。` : ''
      if (!await adminConfirm(`确定删除导航 Tab「${title}」？${extra}`)) return
      this.errorMsg = ''
      try {
        await deleteArticleManageNav(target.id)
        this.showNavDialog = false
        this.selectedNav = null
        await this.reload({ silent: true, resetPage: false })
      } catch (e) {
        this.errorMsg = e.message || '删除失败'
      }
    },
    openCreateContent() {
      if (!this.selectedCategory) return
      const group = this.selectedCategory.displayGroup
      this.articleForm = emptyArticleForm(this.selectedCategory.id, this.selectedNav?.id || null, group)
      this.articleDialogError = ''
      this.showArticleDialog = true
    },
    openEditCategory(cat) {
      this.categoryForm = {
        id: cat.id,
        listCode: cat.listCode || '',
        displayName: cat.displayName || '',
        coverUrl: cat.coverUrl || '',
        displayGroup: cat.displayGroup || 'QUICK',
        sortOrder: cat.sortOrder ?? 0,
        statusCode: cat.statusCode || 'ENABLED',
        openToNonMember: cat.openToNonMember ?? 1,
        remark: '',
      }
      this.categoryDialogError = ''
      this.showCategoryDialog = true
    },
    maybeFillListCode() {
      if (this.categoryForm.listCode?.trim()) return
      const name = (this.categoryForm.displayName || '').trim()
      if (!name) return
      const used = new Set(this.allCategories.map((c) => String(c.listCode || '').trim()))
      let base = name.toLowerCase().replace(/\s+/g, '-').replace(/[^\w\u4e00-\u9fa5-]/g, '').slice(0, 24)
      if (!base) base = `cat-${Date.now()}`
      let code = base
      let i = 1
      while (used.has(code)) {
        code = `${base}-${i++}`
      }
      this.categoryForm.listCode = code
    },
    async saveCategory() {
      const f = this.categoryForm
      this.categoryDialogError = ''
      if (!f.displayName?.trim()) {
        this.categoryDialogError = '请填写分类名称'
        return
      }
      this.maybeFillListCode()
      if (f.displayGroup !== 'NEWS' && !f.coverUrl?.trim()) {
        this.categoryDialogError = '请上传分类图标'
        return
      }
      this.saving = true
      try {
        await saveArticleManageCategory({
          id: f.id,
          listCode: (f.listCode || '').trim(),
          displayName: f.displayName.trim(),
          coverUrl: f.displayGroup === 'NEWS' ? '' : f.coverUrl.trim(),
          displayGroup: f.displayGroup,
          sortOrder: f.sortOrder ?? 0,
          statusCode: f.statusCode,
          openToNonMember: f.openToNonMember,
          remark: '',
        }, !!f.id)
        this.showCategoryDialog = false
        await this.reload({ silent: true, resetPage: false })
      } catch (e) {
        this.categoryDialogError = e.message || '保存失败'
      } finally {
        this.saving = false
      }
    },
    async openEditContent(row) {
      try {
        const rows = await getArticles(this.selectedCategory.id)
        const full = (rows || []).find((r) => r.id === row.id)
        this.openEditArticle(full || row)
      } catch (e) {
        this.errorMsg = e.message || '加载内容失败'
      }
    },
    openEditArticle(row) {
      const group = this.categoryDisplayGroup(row.categoryId || this.selectedCategory?.id)
      let contentType = row.contentType || (group === 'NEWS' ? 'ARTICLE' : 'RICH_TEXT')
      if (group === 'NEWS' && contentType === 'RICH_TEXT') contentType = 'ARTICLE'
      this.articleForm = {
        id: row.id,
        categoryId: this.selectedCategory?.id,
        navId: row.navId || this.selectedNav?.id || null,
        title: row.title || '',
        excerpt: row.excerpt || '',
        coverUrl: row.coverUrl || '',
        contentType,
        wechatUrl: row.wechatUrl || '',
        fileUrl: row.fileUrl || '',
        bodyHtml: normalizeRichHtmlBody(row.bodyHtml || ''),
        publishAt: row.publishAt || nowIsoLocal(),
        viewCount: row.viewCount ?? 0,
        sortOrder: row.sortOrder ?? 0,
        statusCode: row.statusCode || 'ENABLED',
        openToNonMember: row.openToNonMember ?? 1,
        remark: row.remark || '',
      }
      this.articleDialogError = ''
      this.showArticleDialog = true
    },
    async tryResolveTitle(silent) {
      const url = (this.articleForm.wechatUrl || '').trim()
      if (!url) return
      this.resolvingTitle = true
      try {
        const res = await resolveArticleTitleByUrl(url)
        const title = res?.title || ''
        if (title) {
          this.articleForm.title = title
        } else if (!silent) {
          this.articleDialogError = '未能识别标题，请手动填写'
        }
      } catch (e) {
        if (!silent) this.articleDialogError = e.message || '识别失败'
      } finally {
        this.resolvingTitle = false
      }
    },
    pickDoc() {
      this.$refs.docInput?.click()
    },
    async onDocFile(e) {
      const file = e.target.files && e.target.files[0]
      e.target.value = ''
      if (!file) return
      try {
        const data = await uploadFile(file)
        this.articleForm.fileUrl = data?.url || ''
      } catch (err) {
        this.articleDialogError = err.message || '文档上传失败'
      }
    },
    async saveArticle() {
      const f = this.articleForm
      this.articleDialogError = ''
      if (!f.categoryId || !f.title?.trim()) {
        this.articleDialogError = '请填写标题'
        return
      }
      if (!f.publishAt) {
        this.articleDialogError = '请选择发布时间'
        return
      }
      if (f.contentType === 'ARTICLE' && !(f.wechatUrl || '').trim()) {
        this.articleDialogError = '请填写公众号链接'
        return
      }
      if (f.contentType === 'DOC' && !(f.fileUrl || '').trim()) {
        this.articleDialogError = '请上传文档文件'
        return
      }
      if (this.isNewsArticle && f.contentType === 'RICH_TEXT') {
        this.articleDialogError = '资讯动态不支持图文类型'
        return
      }
      this.saving = true
      try {
        const body = {
          categoryId: f.categoryId,
          navId: f.navId || null,
          title: f.title.trim(),
          excerpt: f.excerpt || '',
          coverUrl: f.coverUrl || '',
          contentType: f.contentType,
          wechatUrl: f.contentType === 'ARTICLE' ? (f.wechatUrl || '') : '',
          fileUrl: f.contentType === 'DOC' ? (f.fileUrl || '') : '',
          bodyHtml: f.contentType === 'RICH_TEXT' ? (f.bodyHtml || '') : '',
          publishAt: f.publishAt || null,
          viewCount: f.viewCount ?? 0,
          sortOrder: f.sortOrder ?? 0,
          statusCode: f.statusCode,
          openToNonMember: f.openToNonMember,
          remark: f.remark || '',
        }
        if (f.id) await updateArticle(f.id, body)
        else await createArticle(body)
        this.showArticleDialog = false
        await this.reload({ silent: true, resetPage: false })
      } catch (e) {
        this.articleDialogError = e.message || '保存失败'
      } finally {
        this.saving = false
      }
    },
    async onDeleteContent(row) {
      await this.onDeleteArticle(row)
    },
    async onDeleteArticle(row) {
      if (!await adminConfirm(`确定删除「${row.title}」？`)) return
      try {
        await deleteArticle(row.id)
        await this.reload({ silent: true, resetPage: false })
      } catch (e) {
        this.errorMsg = e.message || '删除失败'
      }
    },
  },
}
</script>

<style scoped>
.card { background: #fff; border-radius: 12px; padding: 18px; }
.head { display: flex; justify-content: space-between; align-items: flex-start; gap: 12px; margin-bottom: 8px; }
.head h2 { margin: 0 0 6px; }
.head-actions { display: flex; gap: 8px; flex-shrink: 0; }
.tip { background: #f5f8ff; border: 1px solid #dae5ff; color: #2a4b8d; padding: 8px 10px; border-radius: 8px; margin: 0; line-height: 1.55; font-size: 13px; }
.layout { display: flex; gap: 14px; margin-top: 12px; min-height: 420px; }
.tree-panel { width: 280px; flex-shrink: 0; border: 1px solid #eef2f7; border-radius: 12px; padding: 8px; background: #fbfcfe; overflow: auto; max-height: 70vh; }
.tree-row { width: 100%; border: none; background: transparent; text-align: left; display: flex; align-items: center; gap: 6px; padding: 7px 8px; border-radius: 8px; cursor: pointer; font-size: 13px; color: #344054; }
.tree-row:hover { background: #eef4ff; }
.tree-row.active { background: #f5f8ff; color: #24438d; }
.tree-l1-row,
.tree-l2-row,
.tree-l3-row {
  display: flex;
  align-items: center;
  gap: 2px;
  padding-right: 4px;
  border-radius: 8px;
}
.tree-l1-row:hover,
.tree-l2-row:hover,
.tree-l3-row:hover { background: #eef4ff; }
.tree-l1-btn {
  flex: 1;
  min-width: 0;
  font-weight: 700;
  color: #475467;
}
.tree-l1-name {
  flex: 1;
  min-width: 0;
  text-align: left;
}
.tree-l1-row:hover .tree-row:hover { background: transparent; }
.tree-l2-row.active,
.tree-l3-row.active {
  background: #f5f8ff;
  color: #24438d;
  box-shadow: inset 0 0 0 1px #c7d7fe;
}
.tree-l3-row.active .tree-leaf,
.tree-l3-row.active .tree-count { color: #667085; }
.tree-l2-btn {
  flex: 1;
  min-width: 0;
  padding-left: 12px;
}
.tree-l3-btn {
  flex: 1;
  min-width: 0;
  padding-left: 28px;
  font-size: 12px;
}
.tree-l2-row.active .tree-row:hover,
.tree-l3-row.active .tree-row:hover { background: transparent; }
.tree-l1-row:hover .tree-row-actions,
.tree-l2-row:hover .tree-row-actions,
.tree-l2-row.active .tree-row-actions,
.tree-l3-row:hover .tree-row-actions,
.tree-l3-row.active .tree-row-actions {
  opacity: 1;
}
.tree-act-btn--primary:hover { background: rgba(47, 106, 246, 0.1); }
.tree-nav-name {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  text-align: left;
}
.tree-l3-body { overflow: hidden; }
.tree-row-actions {
  display: flex;
  align-items: center;
  gap: 2px;
  flex-shrink: 0;
  opacity: 0;
  transition: opacity .15s ease;
}
.tree-l2-row.active .tree-act-btn:hover,
.tree-l3-row.active .tree-act-btn:hover { background: rgba(47, 106, 246, 0.12); }
.tree-act-btn {
  width: 26px;
  height: 26px;
  border: none;
  background: transparent;
  padding: 4px;
  cursor: pointer;
  border-radius: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}
.tree-act-btn:hover { background: rgba(15, 23, 42, 0.06); }
.tree-act-btn:disabled { opacity: .35; cursor: not-allowed; pointer-events: none; }
.tree-act-icon {
  width: 16px;
  height: 16px;
  display: block;
}
.chevron { display: inline-block; width: 14px; font-size: 11px; color: #98a2b3; transform: rotate(0deg); transition: transform .18s ease; flex-shrink: 0; }
.chevron-spacer { display: inline-block; width: 14px; flex-shrink: 0; }
.chevron.open { transform: rotate(90deg); }
.tree-row.active .chevron { color: #667085; }
.link-preview { font-size: 12px; color: #475467; margin: 0; }
.link-preview code { font-size: 11px; background: #f2f4f7; padding: 2px 6px; border-radius: 4px; word-break: break-all; }
.tree-icon { width: 22px; height: 22px; border-radius: 5px; object-fit: cover; flex-shrink: 0; }
.tree-cat-name { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.tree-leaf { color: #98a2b3; width: 14px; flex-shrink: 0; }
.tree-layout-tag { margin-left: auto; font-size: 10px; color: #98a2b3; padding: 1px 6px; border-radius: 4px; background: #f2f4f7; flex-shrink: 0; }
.tree-row.active .tree-layout-tag { background: #eef4ff; color: #475467; }
.tree-count { margin-left: auto; color: #98a2b3; font-size: 11px; }
.tree-row.active .tree-count { color: #667085; }
.tree-empty { font-size: 12px; color: #98a2b3; padding: 4px 28px; margin: 0; }
.collapse-enter-active, .collapse-leave-active { transition: max-height .2s ease, opacity .2s ease; overflow: hidden; }
.collapse-enter-from, .collapse-leave-to { max-height: 0; opacity: 0; }
.collapse-enter-to, .collapse-leave-from { max-height: 1200px; opacity: 1; }
.table-panel { flex: 1; min-width: 0; border: 1px solid #eef2f7; border-radius: 12px; padding: 12px; }
.cat-bar { display: flex; align-items: center; gap: 12px; margin-bottom: 10px; padding-bottom: 10px; border-bottom: 1px solid #eef2f7; }
.cat-icon { width: 44px; height: 44px; border-radius: 8px; object-fit: cover; border: 1px solid #e5e7eb; }
.cat-meta { flex: 1; display: flex; flex-direction: column; gap: 2px; }
.cat-meta small { color: #98a2b3; font-size: 12px; }
.table-toolbar { margin-bottom: 8px; }
.filter-input { border: 1px solid #d0d7e2; border-radius: 8px; padding: 7px 10px; width: 220px; font-size: 13px; }
table { width: 100%; border-collapse: collapse; }
.table-scroll { overflow-x: auto; }
.table-scroll table { min-width: 860px; }
.content-table { border-collapse: separate; border-spacing: 0; }
.content-table .col-actions {
  position: sticky;
  right: 0;
  z-index: 2;
  background: #fff;
  box-shadow: -6px 0 8px -4px rgba(15, 23, 42, 0.12);
  white-space: nowrap;
}
.content-table thead .col-actions {
  z-index: 3;
  background: #fafbfc;
}
.content-table tbody tr:hover .col-actions { background: #f9fafb; }
.content-table .col-actions--empty { box-shadow: none; }
.tree-l1-row--active { background: #eef4ff; border-radius: 8px; }
.tree-l1-row--active .tree-l1-btn { color: #24438d; }
.req { color: #d92d20; margin-left: 2px; }
.dialog-error { color: #d92d20; font-size: 13px; margin: 0 0 10px; }
.status-toggle .btn.disabled,
.status-toggle .btn:disabled { opacity: .45; cursor: not-allowed; }
th, td { border-bottom: 1px solid #edf0f5; padding: 8px; font-size: 13px; text-align: left; }
.thumb { width: 48px; height: 48px; object-fit: cover; border-radius: 6px; }
.ellipsis { max-width: 240px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.muted { color: #98a2b3; font-size: 13px; }
.content-table th,
.content-table td {
  text-align: center;
  vertical-align: middle;
}
.content-filters {
  margin-bottom: 12px;
}
.content-filters .flt-input {
  height: 40px;
  border: 1px solid #d0d7e2;
  border-radius: 8px;
  padding: 0 12px;
  font-size: 13px;
  background: #fff;
  min-width: 220px;
}
.content-filters .flt-input--wide {
  min-width: 280px;
}
.content-create-btn {
  margin-left: auto;
}
.center { text-align: center; }
.pad { padding: 48px 0; }
.pill { display: inline-block; min-width: 48px; text-align: center; padding: 3px 8px; border-radius: 999px; font-size: 12px; font-weight: 600; }
.pill-green { color: #067647; background: #ecfdf3; border: 1px solid #abefc6; }
.pill-red { color: #b42318; background: #fef3f2; border: 1px solid #fecdca; }
.btn { border-radius: 8px; padding: 6px 12px; cursor: pointer; border: 1px solid transparent; font-size: 12px; }
.btn + .btn { margin-left: 6px; }
.btn-primary { background: #2f6af6; color: #fff; }
.btn-edit { border-color: #bfc9db; background: #fff; color: #26457d; }
.btn-delete { border-color: #ffc8c2; background: #fff1f0; color: #d92d20; }
.btn-cancel { border-color: #d0d7e2; background: #fff; color: #344054; }
.btn:disabled { opacity: .55; cursor: not-allowed; }
.status-toggle { display: inline-flex; gap: 8px; flex-wrap: wrap; }
.btn-sm { padding: 6px 10px; }
.status-toggle .btn.active { border-color: #9bb7ff; background: #f5f8ff; color: #24438d; }
.error { color: #d92d20; margin: 8px 0; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.form-grid--create {
  grid-template-columns: 1fr;
  gap: 18px;
}
.dialog--create .field { gap: 8px; }
.dialog--create .field > span { font-size: 14px; }
.dialog--create :deep(.fs-btn) { height: 44px; font-size: 14px; }
.field { display: flex; flex-direction: column; gap: 6px; font-size: 13px; color: #344054; min-width: 0; }
.field > span { font-weight: 600; line-height: 1.45; flex-shrink: 0; }
.field--rich-editor { position: relative; z-index: 2; margin-bottom: 4px; }
.field-hint { margin: 0 0 8px; font-size: 12px; color: #667085; line-height: 1.5; font-weight: 400; }
.form-grid-meta {
  position: relative;
  z-index: 3;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  grid-column: span 2;
  padding-top: 8px;
  background: #fff;
}
.form-grid-meta .full { grid-column: span 2; }
.full { grid-column: span 2; }
input, select, textarea { border: 1px solid #d0d7e2; border-radius: 8px; padding: 8px 10px; background: #fff; font-size: 13px; }
.url-row { display: flex; gap: 8px; align-items: center; }
.url-row input { flex: 1; }
.upload-row { display: flex; align-items: center; gap: 10px; }
.file-link { color: #175cd3; font-size: 12px; text-decoration: none; max-width: 360px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.visually-hidden-file { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
</style>
